import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { OpticalJobCollection } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { useCurrency } from '@/hooks/useCurrency';
import { useRecordTendersMutation, type Tender } from '@/modules/sales/api/sales.api';
import { TenderPanel } from '@/modules/sales/components/TenderPanel';
import { apiErrorCode } from '@/services/apiClient';
import { opticalJobKeys } from '../../api/opticalJobs.api';
import { trackingErrorMessage } from '../../tracking/errors';

interface JobPaymentCardProps {
  jobId: string;
  collection: OpticalJobCollection | undefined;
  isLoading: boolean;
  loadError: unknown;
  /** The user may take money, at this order's own branch. */
  canCollect: boolean;
  /** Named when the order belongs to another branch than the one in use: its balance is taken there. */
  otherBranchName: string | null;
  /** The job was cancelled with its order: it owes nothing because it was refunded, not because it was paid, and nothing is handed over. */
  cancelled: boolean;
  collectOpen: boolean;
  onCollectOpenChange: (open: boolean) => void;
  onCollected: (message: string) => void;
}

/**
 * The order's money as the handover sees it, and the way to take what is owed.
 *
 * Figures are the server's, worked out by the rule the handover applies. The balance is taken through the order's own payments — never an optical payment of its own — at the order's branch, with the counter's tender panel; only the tenders listed are recorded, and an amount typed but not added holds the button back until it is added or cleared. A failed attempt keeps what was entered. An order on a payment plan is paid through its plan.
 */
export function JobPaymentCard({
  jobId,
  collection,
  isLoading,
  loadError,
  canCollect,
  otherBranchName,
  cancelled,
  collectOpen,
  onCollectOpenChange,
  onCollected,
}: JobPaymentCardProps) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const queryClient = useQueryClient();
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const record = useRecordTendersMutation(collection?.orderId ?? '');
  // A second click lands before the button re-renders as busy: only the first may send.
  const inFlight = useRef(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader title={t('opticalOrders.job.paymentTitle')} />
        <LoadingState />
      </Card>
    );
  }
  if (loadError || !collection) {
    return (
      <Card>
        <CardHeader title={t('opticalOrders.job.paymentTitle')} />
        <div className="px-5 py-4">
          <Alert tone="error">{trackingErrorMessage(t, loadError)}</Alert>
        </div>
      </Card>
    );
  }

  const tendered = tenders.reduce((sum, tender) => sum + tender.amountMinor, 0);
  const owing = collection.outstandingMinor;
  const offerCollect = owing > 0 && !collection.plan && canCollect && !otherBranchName;

  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    record.mutate(tenders, {
      onSettled: () => {
        inFlight.current = false;
      },
      onSuccess: () => {
        setTenders([]);
        onCollectOpenChange(false);
        void queryClient.invalidateQueries({ queryKey: opticalJobKeys.collection(jobId) });
        void queryClient.invalidateQueries({ queryKey: opticalJobKeys.lists() });
        onCollected(t('opticalOrders.collect.recorded', { amount: format(tendered) }));
      },
      onError: (caught) => {
        if (apiErrorCode(caught) === 'CONFLICT') {
          // The balance moved since it was read: read it again and let the user decide anew.
          void queryClient.invalidateQueries({ queryKey: opticalJobKeys.collection(jobId) });
          setError(t('opticalOrders.collect.balanceChanged'));
          return;
        }
        setError(trackingErrorMessage(t, caught));
      },
    });
  };

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.job.paymentTitle')}
        subtitle={t('opticalOrders.job.paymentHint')}
      />
      <dl className="space-y-2 px-5 py-4 text-sm">
        <Figure
          label={t('opticalOrders.job.orderTotal')}
          value={format(collection.netObligationMinor)}
        />
        <Figure
          label={t('opticalOrders.job.collected')}
          value={format(collection.netCollectedMinor)}
        />
        <Figure label={t('opticalOrders.job.outstanding')} value={format(owing)} strong />
      </dl>
      <div className="space-y-3 px-5 pb-4">
        {owing === 0 ? (
          cancelled ? null : (
            <Alert tone="success">{t('opticalOrders.job.paidInFull')}</Alert>
          )
        ) : collection.plan ? (
          <Alert tone={collection.handoverCovered ? 'info' : 'warning'}>
            {t(
              collection.handoverCovered
                ? 'opticalOrders.job.planCovers'
                : 'opticalOrders.job.planShort',
              {
                reference: collection.plan.reference,
              },
            )}{' '}
            <Link className="font-medium underline" to={`/sales/plans/${collection.plan.id}`}>
              {t('opticalOrders.job.openPlan')}
            </Link>
          </Alert>
        ) : otherBranchName ? (
          <Alert tone="warning">
            {t('opticalOrders.collect.otherBranch', { branch: otherBranchName })}
          </Alert>
        ) : !canCollect ? (
          <Alert tone="warning">{t('opticalOrders.collect.noPermission')}</Alert>
        ) : null}

        {offerCollect && !collectOpen ? (
          <Button variant="secondary" onClick={() => onCollectOpenChange(true)}>
            {t('opticalOrders.collect.open')}
          </Button>
        ) : null}

        {offerCollect && collectOpen ? (
          <section aria-label={t('opticalOrders.collect.title')} className="space-y-3">
            {error ? <Alert tone="error">{error}</Alert> : null}
            <TenderPanel
              tenders={tenders}
              totalMinor={owing}
              outstandingMinor={Math.max(0, owing - tendered)}
              onChange={setTenders}
              onPendingChange={setPending}
            />
            <p className="text-muted text-sm">{t('opticalOrders.collect.onlyListed')}</p>
            {pending ? <Alert tone="warning">{t('opticalOrders.collect.notAdded')}</Alert> : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setTenders([]);
                  setError(null);
                  onCollectOpenChange(false);
                }}
                disabled={record.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={submit}
                isLoading={record.isPending}
                disabled={tenders.length === 0 || pending}
              >
                {tenders.length === 0
                  ? t('opticalOrders.collect.nothingAdded')
                  : t('opticalOrders.collect.record', { amount: format(tendered) })}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </Card>
  );
}

function Figure({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={strong ? 'text-ink font-medium' : 'text-muted'}>{label}</dt>
      <dd className={strong ? 'text-ink font-semibold' : 'text-ink'} dir="ltr">
        {value}
      </dd>
    </div>
  );
}
